import { useMemo } from 'react';
import { ArrowRightAlt } from '@mui/icons-material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { Button, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Action, useScheduledActionsForTodayQuery } from '../../../../api/gql';
import { ClaimIcon, InvoiceIcon } from '../../../../components/icons/CustomIcons';
import { ActionIcon } from '../../../../components/invoice/ScheduledActionIcon';
import { ActionName } from '../../../../components/invoice/ScheduledActionName';
import { renderCellOrDefault, renderShowCellOrDefault } from '../../../../components/table/renderCellOrDefault';
import { Table } from '../../../../components/table/Table';
import { dateGridColDef } from '../../../../components/table/utils/columnHeader';
import { actionToTransMap } from '../../../../utils/maps';

export const ScheduledActionsTable = () => {
  const { t } = useTranslation('page-admin-my-tasks');
  const { isLoading, data } = useScheduledActionsForTodayQuery();

  const rows = useMemo(
    () =>
      data?.scheduledActionsForToday
        .sort((a, b) => {
          if (a.scheduledAt < b.scheduledAt) return -1;
          return 1;
        })
        .map((task) => {
          return {
            id: task.id,
            plannedAt: task.scheduledAt,
            action: task.action,
            performs: task.action,
            vendor: task.vendorName,
            customer: task.customerName,
            invoiceNumber: task.invoiceNumber,
            invoiceId: task.invoiceId,
            isClaim: task.invoiceClaim,
          };
        }) ?? [],
    [data?.scheduledActionsForToday],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'plannedAt',
        headerName: t('table.plannedAt') as string,
        minWidth: 150,
        ...dateGridColDef,
      },
      {
        field: 'action',
        headerName: t('table.action') as string,
        minWidth: 130,
        valueGetter: (params) => {
          return t(actionToTransMap[params.value as Action], {
            ns: 'translation',
          });
        },
        renderCell: (params) => {
          return (
            <Stack direction={'row'} gap={1} alignItems={'center'}>
              <ActionIcon action={params.row.action} color={'action'} fontSize={'small'} />
              <ActionName action={params.row.action} fontSize={'small'} />
            </Stack>
          );
        },
      },
      {
        field: 'performs',
        headerName: t('table.performs') as string,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) =>
          params.value === Action.Call ? (
            <SupportAgentIcon color={'action'} />
          ) : (
            <SmartToyOutlinedIcon color={'action'} />
          ),
      },
      {
        field: 'isClaim',
        headerName: t('table.remindingObject') as string,
        minWidth: 150,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) =>
          params.value ? (
            <Stack direction={'row'} gap={1} alignItems={'center'}>
              <ClaimIcon color={'action'} />
              <Typography variant={'caption'} color={'action'}>
                {t('claim')}
              </Typography>
            </Stack>
          ) : (
            <Stack direction={'row'} gap={1} alignItems={'center'}>
              <InvoiceIcon color={'action'} />
              <Typography variant={'caption'} color={'action'}>
                {t('invoice')}
              </Typography>
            </Stack>
          ),
      },
      {
        field: 'invoiceNumber',
        headerName: t('table.invoiceNumber') as string,
        minWidth: 130,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'vendor',
        headerName: t('table.vendor') as string,
        flex: 1,
        minWidth: 100,
        renderCell: (params) => renderShowCellOrDefault(!params.row.isClaim, params),
      },
      {
        field: 'customer',
        headerName: t('table.customer') as string,
        flex: 1,
        minWidth: 100,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'invoiceId',
        headerName: '',
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        flex: 1,
        align: 'right',
        minWidth: 340,
        renderCell: (params) => (
          <Stack direction={'row'}>
            {params.row.isClaim ? (
              <Button component={Link} to={`/admin/claim/${params.value}`}>
                {t('claimDetail')}
              </Button>
            ) : (
              <Button component={Link} to={`/admin/invoice/${params.value}`}>
                {t('invoiceDetail')}
              </Button>
            )}
            <Button
              variant={'contained'}
              component={Link}
              to={`/admin/scheduled-action/${params.id}`}
              endIcon={<ArrowRightAlt />}
            >
              {t('taskDetail')}
            </Button>
          </Stack>
        ),
      },
    ],
    [t],
  );

  return (
    <Table
      id={'admin-scheduled-actions'}
      loading={isLoading}
      autoHeight={true}
      rows={rows}
      columns={columns}
      initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
    />
  );
};
